/**
 * Telemetry, tracking, and command (RB §4.6, §5.2).
 *
 * If a space asset's TT&C station is OFFLINE or DESTROYED — or the asset is
 * cyber-ISOLATED — the asset is **uncontrollable**: it may issue no orders, but it
 * continues station-keeping and continues any manoeuvre it was already executing.
 * That is a derived overlay, not an `ActiveEffect`: an uncontrollable satellite is
 * not DEGRADED, it is simply out of reach.
 *
 * The graph must be resolved to a **fixed point** because a communications relay
 * can itself be a TT&C station *and* a client of one (RB §4.6). A relay whose own
 * station is down cannot be commanded, so neither can the satellites it controls.
 *
 * Cycles are a scenario defect that the §10.3 validator will reject at authoring
 * time (M7). Until that exists — and for scenarios written before it — this must
 * not hang: cycles are detected, every asset in one is marked uncontrollable, and
 * a `SCENARIO_DEFECT` event surfaces it in the after-action review rather than
 * leaving a GM to wonder why four satellites stopped responding.
 */
/**
 * As a tuple as well as a union, following the `enums.ts` pattern: the wire enum
 * in `packages/protocol` is built from this, so it cannot drift from the reasons
 * the engine actually produces.
 */
export const CONTROL_LOSS_REASONS = [
    'STATION_OFFLINE',
    'STATION_DESTROYED',
    'STATION_UNCONTROLLABLE',
    'STATION_MISSING',
    'STATION_NOT_A_STATION',
    'STATION_OVER_CAPACITY',
    'TTC_CYCLE',
    'ISOLATED',
];
const sortedIds = (ids) => [...ids].sort();
/**
 * Which clients a station actually controls when it is over `ttcCapacity`
 * (decision D4): the first N by canonical asset id. A scenario that over-subscribes
 * a station is broken, but it must break *predictably* — picking arbitrarily would
 * make the same scenario behave differently between two replays of the same game.
 */
function overCapacityClients(world) {
    const clientsByStation = new Map();
    for (const asset of Object.values(world.assets)) {
        if (asset.segment !== 'SPACE' || !asset.ttcStation)
            continue;
        const list = clientsByStation.get(asset.ttcStation) ?? [];
        list.push(asset.id);
        clientsByStation.set(asset.ttcStation, list);
    }
    const dropped = new Map();
    for (const [stationId, clients] of clientsByStation) {
        const station = world.assets[stationId];
        const capacity = station?.ttcCapacity;
        if (capacity === undefined || capacity === null)
            continue;
        if (clients.length <= capacity)
            continue;
        for (const clientId of sortedIds(clients).slice(capacity)) {
            dropped.set(clientId, 'STATION_OVER_CAPACITY');
        }
    }
    return dropped;
}
/** Assets that sit on a cycle of TT&C assignments, found by iterative DFS. */
function findCycleMembers(world) {
    const onCycle = new Set();
    const state = new Map();
    for (const start of sortedIds(Object.keys(world.assets))) {
        if (state.get(start))
            continue;
        const path = [];
        const indexInPath = new Map();
        let current = start;
        while (current !== undefined) {
            if (state.get(current) === 'DONE')
                break;
            const seenAt = indexInPath.get(current);
            if (seenAt !== undefined) {
                for (const member of path.slice(seenAt))
                    onCycle.add(member);
                break;
            }
            indexInPath.set(current, path.length);
            path.push(current);
            state.set(current, 'VISITING');
            const next = world.assets[current]?.ttcStation ?? null;
            current = next ?? undefined;
        }
        for (const member of path)
            state.set(member, 'DONE');
    }
    return onCycle;
}
/**
 * Resolve control for the whole world. Pure, and bounded by asset count: the
 * propagation loop can run at most once per asset before it stabilises.
 */
export function resolveControl(world, statuses, isolated = new Set()) {
    const uncontrollable = new Map();
    const defects = [];
    const cycleMembers = findCycleMembers(world);
    if (cycleMembers.size > 0) {
        const members = sortedIds([...cycleMembers]);
        defects.push({
            kind: 'TTC_CYCLE',
            assets: members,
            detail: 'TT&C assignments form a cycle. The §10.3 scenario validator rejects this; ' +
                'until then every asset on the cycle is treated as uncontrollable.',
        });
        for (const id of members)
            uncontrollable.set(id, 'TTC_CYCLE');
    }
    for (const [id, reason] of overCapacityClients(world)) {
        if (!uncontrollable.has(id))
            uncontrollable.set(id, reason);
    }
    const overCapacity = sortedIds([...uncontrollable].filter(([, r]) => r === 'STATION_OVER_CAPACITY').map(([id]) => id));
    if (overCapacity.length > 0) {
        defects.push({
            kind: 'TTC_OVER_CAPACITY',
            assets: overCapacity,
            detail: 'More satellites assigned to a TT&C station than its ttcCapacity allows.',
        });
    }
    for (const asset of Object.values(world.assets)) {
        if (isolated.has(asset.id))
            uncontrollable.set(asset.id, 'ISOLATED');
        if (asset.segment !== 'SPACE')
            continue;
        if (uncontrollable.has(asset.id))
            continue;
        if (!asset.ttcStation) {
            uncontrollable.set(asset.id, 'STATION_MISSING');
            defects.push({
                kind: 'TTC_MISSING',
                assets: [asset.id],
                detail: `${asset.id} has no TT&C station assigned (§10.3).`,
            });
            continue;
        }
        const station = world.assets[asset.ttcStation];
        if (!station) {
            uncontrollable.set(asset.id, 'STATION_MISSING');
            defects.push({
                kind: 'TTC_MISSING',
                assets: [asset.id],
                detail: `${asset.id} references TT&C station ${asset.ttcStation}, which does not exist.`,
            });
            continue;
        }
        if (station.ttcCapacity === undefined || station.ttcCapacity === null) {
            uncontrollable.set(asset.id, 'STATION_NOT_A_STATION');
            defects.push({
                kind: 'TTC_NOT_A_STATION',
                assets: [asset.id],
                detail: `${asset.id} is assigned to ${station.id}, which declares no ttcCapacity.`,
            });
            continue;
        }
        const stationStatus = statuses[station.id] ?? 'NOMINAL';
        if (stationStatus === 'DESTROYED')
            uncontrollable.set(asset.id, 'STATION_DESTROYED');
        else if (stationStatus === 'OFFLINE')
            uncontrollable.set(asset.id, 'STATION_OFFLINE');
    }
    // Propagate to a fixed point: a relay that cannot be commanded cannot pass
    // commands on. Bounded by asset count — each pass must mark at least one new
    // asset or it is the last pass.
    const limit = Object.keys(world.assets).length + 1;
    for (let pass = 0; pass < limit; pass++) {
        let changed = false;
        for (const asset of Object.values(world.assets)) {
            if (asset.segment !== 'SPACE' || uncontrollable.has(asset.id))
                continue;
            const station = asset.ttcStation ? world.assets[asset.ttcStation] : undefined;
            if (station && uncontrollable.has(station.id)) {
                uncontrollable.set(asset.id, 'STATION_UNCONTROLLABLE');
                changed = true;
            }
        }
        if (!changed)
            break;
    }
    return {
        uncontrollable: Object.fromEntries([...uncontrollable].sort(([a], [b]) => (a < b ? -1 : 1))),
        defects,
    };
}
export function isControllable(control, id) {
    return control.uncontrollable[id] === undefined;
}
//# sourceMappingURL=ttc.js.map